import { splitCareFocusSelection } from "./careFocusSelection";

/**
 * Canonical care-lens keys. Every surface that adapts to the care focus
 * (Profile sections, Quick Log, Care Circle, Ask Record, reports) must
 * resolve the lens through getCareLensKey() — never by scanning
 * conditions/supports text on its own.
 */
export const CARE_LENS = {
  AUTISM: "autism",
  DEMENTIA: "dementia",
  CHRONIC_ILLNESS: "chronic_illness",
  HOSPICE: "hospice",
  GENERAL: "general",
};

const CODE_TO_LENS = {
  ASD: CARE_LENS.AUTISM,
  AUTISM: CARE_LENS.AUTISM,
  DEMENTIA: CARE_LENS.DEMENTIA,
  CHRONIC: CARE_LENS.CHRONIC_ILLNESS,
  CHRONIC_ILLNESS: CARE_LENS.CHRONIC_ILLNESS,
  HOSPICE: CARE_LENS.HOSPICE,
  // Palliative care intentionally shares the Hospice lens for now.
  PALLIATIVE_CARE: CARE_LENS.HOSPICE,
  PALLIATIVE: CARE_LENS.HOSPICE,
  GENERAL_CARE: CARE_LENS.GENERAL,
  GENERAL: CARE_LENS.GENERAL,
};

const LENS_TEXT_PATTERNS = [
  [CARE_LENS.HOSPICE, /\b(hospice|palliative|end of life|comfort care)\b/],
  [CARE_LENS.DEMENTIA, /\b(dementia|memory care|alzheimer'?s?)\b/],
  [CARE_LENS.CHRONIC_ILLNESS, /\b(chronic (illness|condition)|medical complexity|complex medical|multiple sclerosis|epilepsy|seizure)\b/],
  [CARE_LENS.AUTISM, /\b(autism|autistic|asd)\b/],
  [CARE_LENS.GENERAL, /\bgeneral care\b/],
];

const itemText = (item) => {
  if (item == null) return "";
  if (typeof item === "string") return item;
  return String(item.label || item.name || item.title || item.value || "");
};

const itemCode = (item) => {
  if (item == null) return "";
  if (typeof item === "string") return item.toUpperCase().replace(/\s+/g, "_");
  return String(item.code || "").toUpperCase();
};

const resolveLensFromItem = (item) => {
  if (item == null) return null;

  const byCode = CODE_TO_LENS[itemCode(item)];
  if (byCode) return byCode;

  const text = itemText(item).trim().toLowerCase();
  if (!text) return null;
  const byText = LENS_TEXT_PATTERNS.find(([, pattern]) => pattern.test(text));
  return byText ? byText[0] : null;
};

const asArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

const getStoredSelection = (profile = {}) => (
  asArray(profile?.careContext?.careFocus).length
    ? profile.careContext.careFocus
    : profile?.selectedConditions || profile?.conditions || profile?.concerns || []
);

/**
 * Resolves the single care lens for a profile.
 *
 * Resolution order:
 * 1. Explicit careContext.primaryCareFocus (authoritative when present)
 * 2. The primary focus inside the stored selection
 *    (careContext.careFocus, else conditions/concerns), via
 *    splitCareFocusSelection — additional supports and durable
 *    condition context never influence the lens.
 * 3. Legacy free-text careFocus values (e.g. "Dementia support")
 * 4. CARE_LENS.GENERAL
 *
 * Always returns exactly one lens key.
 */
export const getCareLensKey = (profile = {}) => {
  const explicitLens = resolveLensFromItem(profile?.careContext?.primaryCareFocus);
  if (explicitLens) return explicitLens;

  const storedSelection = getStoredSelection(profile);
  const { primaryFocus } = splitCareFocusSelection(storedSelection);
  const primaryLens = resolveLensFromItem(primaryFocus);
  if (primaryLens) return primaryLens;

  // Legacy fallback: free-text primary focus entries that predate codes.
  const legacyLens = asArray(storedSelection)
    .map(resolveLensFromItem)
    .find(Boolean);
  if (legacyLens) return legacyLens;

  return CARE_LENS.GENERAL;
};
